import { toast } from 'react-toastify';
import { all, call, fork, put, select, takeLeading, takeLatest } from 'redux-saga/effects';
import { isUndefined } from 'lodash/fp';

import { deleteRoleFailed, deleteRoleStarted, deleteRoleSuccessful, fetchAllRolesFailed, fetchAllRolesStarted, fetchAllRolesSuccessful, upsertRoleFailed, upsertRoleSuccessful } from './actions';
import { getSelectedRole } from './selectors';
import { Role, RolesActionTypes } from './types';
import { deleteRole, getAllRoles, upsertRole } from '../../../../services/role';
import { fetchAllUsersStarted } from '../../users/store';

export function* attemptUpsertRole() {
    try {
        const selectedRole: Role = yield select(getSelectedRole);
        const allRoles: Role[] = yield call(upsertRole, selectedRole);
        const isExisting = !isUndefined(selectedRole.roleId);
        {
            yield put(upsertRoleSuccessful(allRoles));
        }
        const successToastMessage = isExisting ? 'Successfully updated role details' : 'Successfully added a new role';
        toast(successToastMessage);
    } catch (e) {
        yield put(upsertRoleFailed((e as Error).message));
        toast.error('Unable to save role. Please try again.');
    }
}

function* upsertRoleWatcher() {
    yield takeLeading(RolesActionTypes.UPSERT_ROLE_STARTED, attemptUpsertRole);
}

export function* attemptFetchAllRoles({ payload }: ReturnType<typeof fetchAllRolesStarted>) {
    try {
        const { clientId } = payload;
        const roles: Role[] = yield call(getAllRoles, { clientId });
        yield put(fetchAllRolesSuccessful(roles));
    } catch (e) {
        toast.error('Unable to fetch the latest list of roles. Please try again.');
        yield put(fetchAllRolesFailed((e as Error).message));
    }
}

function* fetchAllRolesWatcher() {
    yield takeLatest(RolesActionTypes.FETCH_ALL_ROLES_STARTED, attemptFetchAllRoles);
}

export function* attemptDeleteRole({ payload }: ReturnType<typeof deleteRoleStarted>) {
    try {
        const { roleId } = payload;
        const allRoles: Role[] = yield call(deleteRole, { roleId });
        yield put(deleteRoleSuccessful(allRoles));
        // Refresh users list in case any users were assigned to this role
        yield put(fetchAllUsersStarted());
        toast('Role successfully deleted.');
    } catch (e) {
        toast.error('Unable to delete role. Please try again.');
        yield put(deleteRoleFailed((e as Error).message));
    }
}

function* deleteRoleWatcher() {
    yield takeLeading(RolesActionTypes.DELETE_ROLE_STARTED, attemptDeleteRole);
}

export function* roleSaga() {
    yield all([
        fork(upsertRoleWatcher),
        fork(fetchAllRolesWatcher),
        fork(deleteRoleWatcher)
    ]);
}
